/** The app: who's signed in decides between signing in and the screens below. */
import { CarIcon, ListIcon, UserIcon, WalletIcon } from "@taxi/mobile-web/icons";
import { Link, match, navigate, useLocation } from "@taxi/mobile-web/router";
import { Button, Loading, ToastProvider } from "@taxi/mobile-web/ui";
import { type ReactNode, useEffect } from "react";
import { DriverProvider, useDriver } from "./lib/driver";
import { SessionProvider, useSession } from "./lib/session";
import { AccountScreen } from "./screens/account";
import { DriveScreen } from "./screens/drive";
import { JobScreen } from "./screens/job";
import { JobsScreen } from "./screens/jobs";
import { MoneyScreen, SettlementScreen } from "./screens/money";
import { OfferOverlay } from "./screens/offer";
import { SignInScreen } from "./screens/sign-in";

type Route = { path: string; render: (params: Record<string, string>) => ReactNode; nav?: boolean };

const ROUTES: Route[] = [
  { path: "/", render: () => <DriveScreen />, nav: true },
  { path: "/jobs", render: () => <JobsScreen />, nav: true },
  { path: "/jobs/:id", render: ({ id }) => <JobScreen key={id} id={id!} /> },
  { path: "/money", render: () => <MoneyScreen />, nav: true },
  { path: "/money/settlements/:id", render: ({ id }) => <SettlementScreen id={id!} /> },
  { path: "/account", render: () => <AccountScreen />, nav: true },
];

function BottomNav({ path }: { path: string }) {
  const { jobs } = useDriver();
  const waiting = (jobs?.queue.length ?? 0) + (jobs?.current ? 1 : 0);
  const tabs = [
    { to: "/", label: "Drive", icon: <CarIcon />, active: path === "/" },
    {
      to: "/jobs",
      label: "Jobs",
      badge: waiting,
      icon: <ListIcon />,
      active: path.startsWith("/jobs"),
    },
    { to: "/money", label: "Money", icon: <WalletIcon />, active: path.startsWith("/money") },
    { to: "/account", label: "Account", icon: <UserIcon />, active: path.startsWith("/account") },
  ];
  return (
    <nav className="bottom-nav" aria-label="Main">
      {tabs.map((tab) => (
        <Link
          key={tab.to}
          to={tab.to}
          replace
          className={tab.active ? "active" : ""}
          aria-current={tab.active ? "page" : undefined}
        >
          {tab.icon}
          <span>{tab.label}</span>
          {"badge" in tab && tab.badge ? (
            <>
              <span className="nav-badge" aria-hidden="true">
                {tab.badge}
              </span>
              <span className="sr-only">, {tab.badge} waiting</span>
            </>
          ) : null}
        </Link>
      ))}
    </nav>
  );
}

function SignedIn({ path }: { path: string }) {
  const { jobs } = useDriver();
  const offer = jobs?.offer ?? null;
  for (const route of ROUTES) {
    const params = match(route.path, path);
    if (params) {
      return (
        <>
          <main className={route.nav ? "screen has-nav" : "screen"}>{route.render(params)}</main>
          {route.nav && <BottomNav path={path} />}
          {offer && <OfferOverlay key={offer.id} offer={offer} />}
        </>
      );
    }
  }
  return (
    <main className="screen">
      <div className="empty">
        <h2>Page not found</h2>
        <Button onClick={() => navigate("/", { replace: true })}>Back to driving</Button>
      </div>
    </main>
  );
}

function Screens() {
  const { me } = useSession();
  const { path, query } = useLocation();

  // Signed out: everything waits behind signing in, then carries on where it was going.
  const next = query.get("next");
  useEffect(() => {
    if (!me && path !== "/sign-in") {
      const here = `${path}${query.size ? `?${query}` : ""}`;
      navigate(here === "/" ? "/sign-in" : `/sign-in?next=${encodeURIComponent(here)}`, {
        replace: true,
      });
    } else if (me && path === "/sign-in") {
      // Only somewhere in this app.
      const inApp = next?.startsWith("/") && !next.startsWith("//") && !next.includes("\\");
      navigate(inApp ? next! : "/", { replace: true });
    }
  }, [me, path, query, next]);

  if (!me) return path === "/sign-in" ? <SignInScreen /> : <Loading />;
  if (path === "/sign-in") return <Loading />;
  return (
    <DriverProvider>
      <SignedIn path={path} />
    </DriverProvider>
  );
}

export function App() {
  return (
    <ToastProvider>
      <SessionProvider
        loading={<Loading label="Starting up" />}
        failed={(retry) => (
          <main className="screen">
            <div className="empty">
              <h2>We can't connect right now</h2>
              <p>Check your connection and try again.</p>
              <Button onClick={retry}>Try again</Button>
            </div>
          </main>
        )}
      >
        <Screens />
      </SessionProvider>
    </ToastProvider>
  );
}
